import { BCMSCurrency, BCMSDate, BCMSRecord, BCMSStateManagerCRUD, BCMSValidate, Branding, BrandingStyle, Link, useBCMSContext, useModel, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import { ContentHeaderView, FormRowView, PageContainer, PageContentView, WidgetContainer } from "components/shared";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton } from "components/ui";
import { getDefaultStopPayment } from "components/workflows/StopPaymentsWorkflow";
import StopPayment from "components/workflows/StopPaymentsWorkflow/classes/StopPayment";
import Classes, { Account, CheckingAccount, LOCAccount, StopPayment as StopPaymentType } from "model";
import React from "react";

import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import StopPaymentHistoryWidget from "components/shared/widgets/StopPaymentHistoryWidgets/StopPaymentHistoryWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function StopPaymentConfirmPage
 * @return {React.Component}
 * @exported true
 * @description The main component for StopPaymentConfirmPage
 */
export default function StopPaymentConfirmPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const { branding } = useModel();
    const { options } = branding;
    const data = { ...getDefaultPageData(options), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";
    const { prevStep } = useWorkflow() ?? {};
    const getFilteredStopPayments = accountID => StopPaymentType.initAll().filter(item => item.account.id === accountID);

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        // START: functions for the stop payment history widget.
        onStopPaymentWidgetHistoryChange: prop => val => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "stopPaymentHistoryWidget", prop ], val)
            .submit(setState);
        },

        onStopPaymentWidgetHistoryClick: evt => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "stopPaymentHistoryWidget", "showPayments" ], true)
                .update([ "data", "stopPaymentHistoryWidget", "payments" ], getFilteredStopPayments(state.data.stopPaymentHistoryWidget.account.id))
                .submit(setState);
        },
        // END: functions for the stop payment history widget.

        onBack: evt => {
            let defaultStopPayment = getDefaultStopPayment(globalController);
            
            // Keep the state as is in the "stop payment history" widget at all times.
            defaultStopPayment.data.stopPaymentHistoryWidget = state.data.stopPaymentHistoryWidget;
            defaultStopPayment.ui.stopPaymentHistoryWidget = state.ui.stopPaymentHistoryWidget;
            window.scrollTo(0, 0);
            prevStep(defaultStopPayment);
        } 
    });

    return (
        <PageContainer className={"stop-payment-confirm-page"+className} state={{ data, ui }} controller={controller}>
            <StopPaymentConfirmContent />
        </PageContainer>
    );
}

export const getDefaultPageData = (brandingOptions, stopPaymentHistoryWidgetProps) => {
    let options = brandingOptions ?? {};
    let stopPaymentFeeAmount = options.stop_payment_fee_amount_abi22;
    stopPaymentFeeAmount = stopPaymentFeeAmount === "undefined" ? "X.XX" : stopPaymentFeeAmount;
    stopPaymentFeeAmount = BCMSValidate.isNumber(stopPaymentFeeAmount)
    ? BCMSCurrency.init(stopPaymentFeeAmount).format()
    : stopPaymentFeeAmount;

    // START: stop payment history widget data.
    const account = stopPaymentHistoryWidgetProps?.accountID ? Account.initByType(stopPaymentHistoryWidgetProps?.accountID) : Account.getDefaultBrandedAccountByType("checking");
    let _accounts = Account.hasLOCAccounts() ? LOCAccount.initAll() : CheckingAccount.initAll();
    _accounts = stopPaymentHistoryWidgetProps?.accounts ?? _accounts;
    let _payments = StopPaymentType.initAll().filter(item => item.account.id === account.id);
    _payments = stopPaymentHistoryWidgetProps?.payments ?? _payments;
    const _allStopPayments = stopPaymentHistoryWidgetProps?.allStopPayments ?? StopPaymentType.initAll();
    // END: stop payment history widget data.

    return {
        payment: StopPayment.init({}),
        stopPaymentFeeAmount: stopPaymentFeeAmount,
        stopPaymentHistoryWidget: {  
            account, 
            accounts: _accounts, 
            payments: _payments, 
            allStopPayments: _allStopPayments 
        }
    }
};
export const getDefaultPageUI = () => ({
    stopPaymentHistoryWidget: {
        showPayments: false,
        accountSelect: { isActive: false }
    }
});

function StopPaymentConfirmContent(props) {
    const { data, ui, onBack, onStopPaymentWidgetHistoryChange, onStopPaymentWidgetHistoryClick } = useStateManager();
    const stopPayment = data.payment;
    const { branding } = useModel();

    let checkNumbers = stopPayment.paymentType === "Single Check" ? stopPayment.checkNumber : stopPayment.startCheckNumber + "-" + stopPayment.endCheckNumber;
    
    if(stopPayment.paymentType === "Single Check" && stopPayment.checkNumber === "") {
        checkNumbers = "";
    }
    
    let amount = stopPayment.amount instanceof BCMSCurrency ? stopPayment.amount.format() : BCMSCurrency.init(stopPayment.amount).format();
    amount = stopPayment.amount === "" ? "" : amount;
    const feeMessage = `A fee of ${ data.stopPaymentFeeAmount } applies for stop payment.`;
    const promoText = branding.options.stop_payment_promo_abi22;

    return (
        <PageContentView>
            <InfoBanner text={"A stop payment for check " + checkNumbers + " has successfully been placed on " + stopPayment.account.nickname} />
            {/* Banner to add */}
            {promoText && <CustomContentHeaderWidget content={promoText} />}
            <Branding id="stop_payment_fee_abi22" value="true" default="false">
                <BrandingStyle style={{ "color" : "stop_payment_fee_text_abi22" }} className="stop-payment-fee" element={ <p>{ feeMessage }</p> } />          
            </Branding>
            <WidgetContainer className="stop-payment-confirmation-widget">
                <ContentHeaderView text="Stop Payment" />
                <Branding id="stoppayment_history_widget_abi22" value="false" default="false">
                    <Link to="/stopPaymentHistory" className="stop-payment-history-link" dataPoint="Stop_Payment_History">Stop Payment History</Link>
                </Branding>
                <div className="flex-container flex-horizontal details-container">
                    <div className="flex-container flex-vertical set-width left-detail-container">
                        <b className="text-right detail-label">Tracking ID:</b>
                        <b className="text-right detail-label">Account:</b>
                        <b className="text-right detail-label">Amount:</b>
                        <b className="text-right detail-label">Check(s):</b>
                        <b className="text-right detail-label">Scheduled Date & Time:</b>
                    </div>
                    <div className="flex-container flex-vertical flex-width right-detail-container">
                        <p className="detail-text">1111</p>
                        <p className="detail-text">{stopPayment.account.number}</p>
                        <p className="detail-text">{amount}</p>
                        <p className="detail-text">{checkNumbers}</p>
                        <p className="detail-text">{BCMSDate.init().format("m/d/Y h:m:s")} AM</p>
                    </div>
                </div>
                <FormRowView>
                    <PrimaryButton text="Back" onClick={onBack} />
                </FormRowView>
            </WidgetContainer>
            <Branding id="stoppayment_history_widget_abi22" value="true" default="false">
                <StopPaymentHistoryWidget data={ data.stopPaymentHistoryWidget } ui={ ui.stopPaymentHistoryWidget }
                onChange={ onStopPaymentWidgetHistoryChange } onClick={ onStopPaymentWidgetHistoryClick } />
            </Branding>
        </PageContentView>
    );
}